import apiClient from "./apiClient";

/**
 * Shop & folder API for seller storefronts.
 * Expected backend:
 * - GET/PATCH /shop/ (seller's shop; GET with ?user_id= for public)
 * - GET/POST/PATCH/DELETE /shop/folders/ or /folders/
 * - Listings may have folder (id) and support ?folder= for filtering
 */

const SHOP_BASE = "/shop";
const FOLDERS_BASE = "/shop/folders";

export const shopApi = {
  /** Get shop by user_id (public storefront). Returns null if no shop or 404. */
  async getByUserId(userId) {
    if (!userId) return null;
    try {
      const res = await apiClient.get(`${SHOP_BASE}/?user=${encodeURIComponent(userId)}`);
      if (res.ok && res.data) {
        const data = Array.isArray(res.data.results) ? res.data.results[0] : res.data;
        return data || null;
      }
      return null;
    } catch (e) {
      return null;
    }
  },

  /** Get current user's shop (for seller dashboard). */
  async getMine(userid) {
    try {
      const res = await apiClient.get(`${SHOP_BASE}/?user=${encodeURIComponent(userid)}`);
      if (res.ok && res.data) {
        const data = Array.isArray(res.data.results) ? res.data.results[0] : res.data;
        return data || null;
      }
      return null;
    } catch (e) {
      return null;
    }
  },

  /** Update current user's shop. FormData or object with banner, shop_name, shop_info, contact_email, contact_phone, about_image, about_text. */
  async updateMine(payload ,id) {
    const isFormData = payload instanceof FormData;
    const res = isFormData
      ? await apiClient.patch(`${SHOP_BASE}/${id}`, payload)
      : await apiClient.patch(`${SHOP_BASE}/${id}`, payload);
    if (!res.ok) throw new Error(res.data?.detail || "Failed to update shop");
    return res.data;
  },

  /** Create shop for current user (if backend uses create-on-first-edit). */
  async createMine(payload) {
    const isFormData = payload instanceof FormData;
    const res = isFormData
      ? await apiClient.post(`${SHOP_BASE}/`, payload)
      : await apiClient.post(`${SHOP_BASE}/`, payload);
    if (!res.ok) throw new Error(res.data?.detail || "Failed to create shop");
    return res.data;
  },
};

export const foldersApi = {
  /** List folders for a seller (public: by user_id). */
  async listByUserId(userId) {
    if (!userId) return [];
    try {
      const res = await apiClient.get(`${FOLDERS_BASE}/?user=${encodeURIComponent(userId)}`);
      if (res.ok && res.data) {
        const list = res.data.results ?? (Array.isArray(res.data) ? res.data : []);
        return list;
      }
      return [];
    } catch (e) {
      return [];
    }
  },

  /** List folders for current user (seller dashboard). */
  async listMine(userid) {
    try {
      const res = await apiClient.get(`${FOLDERS_BASE}/?user=${encodeURIComponent(userid)}`);
      if (res.ok && res.data) {
        const list = res.data.results ?? (Array.isArray(res.data) ? res.data : []);
        return list;
      }
      return [];
    } catch (e) {
      return [];
    }
  },

  /** Create folder. Body: { name } */
  async create(payload) {
    const res = await apiClient.post(`${FOLDERS_BASE}/`, payload);
    if (!res.ok) throw new Error(res.data?.detail || "Failed to create folder");
    return res.data;
  },

  /** Update folder (rename). */
  async update(id, payload) {
    const res = await apiClient.patch(`${FOLDERS_BASE}/${id}/`, payload);
    if (!res.ok) throw new Error(res.data?.detail || "Failed to update folder");
    return res.data;
  },

  /** Delete folder. */
  async delete(id) {
    const res = await apiClient.delete(`${FOLDERS_BASE}/${id}/`);
    if (!res.ok) throw new Error(res.data?.detail || "Failed to delete folder");
    return res.data;
  },
};

export default shopApi;
